import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDesign, type Design } from '../context/DesignContext';
import { getProfile } from '../api/client';
import { formatPhone } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';

export default function ProfilePage() {
  const { user, kids, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { design, setDesign } = useDesign();
  const [drivingStats, setDrivingStats] = useState<{ pickupDrives: number; returnDrives: number; totalDrives: number; lastDrive: string | null } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProfile()
      .then(data => setDrivingStats(data.drivingStats))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-lg mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4 dark:text-white">Profile</h1>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
        <p className="text-lg font-medium dark:text-white">{user.name}</p>
        <p className="text-gray-500 dark:text-gray-400">{formatPhone(user.phoneNumber)}</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 capitalize">{user.role}</p>
      </div>

      {kids.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <h2 className="font-semibold mb-2 dark:text-white">My Kids</h2>
          {kids.map(k => (
            <p key={k.id} className="py-1 dark:text-gray-300">{k.name}</p>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-4"><LoadingSpinner /></div>
      ) : drivingStats && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <h2 className="font-semibold mb-2 dark:text-white">Driving Stats</h2>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{drivingStats.pickupDrives}</p>
              <p className="text-xs text-gray-500">Pickups</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{drivingStats.returnDrives}</p>
              <p className="text-xs text-gray-500">Returns</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{drivingStats.totalDrives}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
        <h2 className="font-semibold mb-2 dark:text-white">Appearance</h2>
        <div className="flex gap-2">
          {(['system', 'light', 'dark'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                theme === t
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
        <h2 className="font-semibold mb-2 dark:text-white">Design Style</h2>
        <div className="flex gap-2">
          {([['A', 'Clean Card'], ['B', 'Compact Dashboard'], ['C', 'Bold & Playful']] as const).map(([d, label]) => (
            <button
              key={d}
              onClick={() => setDesign(d as Design)}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                design === d
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={logout}
        className="w-full py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-medium"
      >
        Log Out
      </button>
    </div>
  );
}
